// Centralised list registry for the CRAM Configuration portal (Task 5).
//
// One row per seeded list in `cram_lists/{listId}`. The `mode` column drives
// the editor surface used by `CramListEditor.tsx`:
//   - "view"        => read-only (e.g. direct_high_individuals; edits are
//                      managed via Firestore + backend code).
//   - "score_only"  => locked rows; only the numeric score (and FATF flag
//                      for geography) is editable.
//   - "full"        => add/remove rows + edit labels (EN/AR) + edit score.
//   - "no_score"    => add/remove rows + edit labels (EN/AR); no score
//                      column (used by `sectors`, which are reference data
//                      for the industries list and are never scored).
//
// See docs/CRAM_PLAN.md §5 for the original task spec.

import { CramRating } from "../../types";

export type CramListMode = "view" | "score_only" | "full" | "no_score";

export interface CramListMeta {
    id: string;
    labelEn: string;
    description: string;
    mode: CramListMode;
    // True when items use scoreYes / scoreNo instead of a single score
    // (direct_high_individuals shape).
    isYesNo?: boolean;
    // True when items expose a FATF flag column (geography only).
    showFatfFlag?: boolean;
    // True when items show Arabic labels (false hides the AR column).
    showArabic?: boolean;
    // When set, the editor renders a Sector dropdown sourced from this
    // referenced list ID (used by `industry_sectors` to point at `sectors`).
    sectorRefListId?: string;
}

export const CRAM_LISTS: CramListMeta[] = [
    {
        id: "customer_segmentation",
        labelEn: "Customer Segmentation",
        description: "Maps clientNature buckets to a CRAM customer-type score.",
        mode: "score_only",
        showArabic: true,
    },
    {
        id: "employment_status",
        labelEn: "Employment Status",
        description:
            "Singles + unordered pairs of the KYC employment_status array. " +
            "Doc IDs are auto-generated from the values; rows are not added or removed here.",
        mode: "score_only",
        showArabic: true,
    },
    {
        id: "occupation",
        labelEn: "Occupation",
        description: "Predefined occupation list (Excel-driven).",
        mode: "full",
        showArabic: true,
    },
    {
        id: "sectors",
        labelEn: "Sectors",
        description:
            "Sector categories used by Industries. Sectors are reference data only — they are not scored.",
        mode: "no_score",
        showArabic: true,
    },
    {
        id: "industry_sectors",
        labelEn: "Industries",
        description:
            "Industry list used by the industryType category. Each industry references a Sector and carries its own risk score.",
        mode: "full",
        showArabic: true,
        sectorRefListId: "sectors",
    },
    {
        id: "direct_high_individuals",
        labelEn: "Direct High — Individuals",
        description:
            "Read-only here. Edits to Direct High items are managed via Firestore / backend code.",
        mode: "view",
        isYesNo: true,
        showArabic: false,
    },
    {
        id: "geography",
        labelEn: "Geography",
        description:
            "Per-country risk score + FATF flag. Country list is seeded from CSV and cannot be added to from here.",
        mode: "score_only",
        showFatfFlag: true,
        showArabic: false,
    },
    {
        id: "products_services",
        labelEn: "Products & Services",
        description:
            "Auto-managed from funds + config/portfolio_service. Only the score can be edited.",
        mode: "score_only",
        showArabic: true,
    },
];

export function getListMeta(id: string): CramListMeta | undefined {
    return CRAM_LISTS.find((l) => l.id === id);
}

// Client-side bucket lookup: mirrors the engine's logic in
// shared-helpers/src/cramHelper.ts — bucket by each band's `min`, highest
// first. Returns null when buckets are not loaded yet.
export function ratingForScore(
    score: number | undefined,
    buckets?: {
        low: { min: number; max: number };
        medium: { min: number; max: number };
        high: { min: number; max: number };
        prohibited: { min: number; max: number };
    }
): CramRating | null {
    if (score == null || !buckets) return null;
    if (score >= buckets.prohibited.min) return "Prohibited";
    if (score >= buckets.high.min) return "High";
    if (score >= buckets.medium.min) return "Medium";
    return "Low";
}

export function ratingBadgeColor(rating: CramRating | null): string {
    switch (rating) {
        case "Low":
            return "green";
        case "Medium":
            return "yellow";
        case "High":
            return "orange";
        case "Prohibited":
            return "red";
        default:
            return "gray";
    }
}

// Geography (country) per-ITEM rating label, derived from the item's own score.
// DISPLAY-ONLY for the Geography list — other lists use ratingForScore +
// config.buckets (no "Very High" band). Never feeds score bucketing:
//   1 = Low, 2 = Medium, 3 = High, 4 = Very High, 8 = Prohibited.
export type CramItemRating =
    | "Low"
    | "Medium"
    | "High"
    | "Very High"
    | "Prohibited";

export function itemRatingForScore(
    score: number | undefined
): CramItemRating | null {
    if (score == null || Number.isNaN(score)) return null;
    if (score >= 8) return "Prohibited";
    if (score >= 4) return "Very High";
    if (score >= 3) return "High";
    if (score >= 2) return "Medium";
    if (score >= 1) return "Low";
    return null;
}

export function itemRatingBadgeColor(rating: CramItemRating | null): string {
    switch (rating) {
        case "Low":
            return "green";
        case "Medium":
            return "yellow";
        case "High":
            return "orange";
        case "Very High":
            return "red";
        case "Prohibited":
            return "grape";
        default:
            return "gray";
    }
}
